import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import CorporateFareIcon from '@mui/icons-material/CorporateFare'

import { Box, Typography } from '@mui/material'
import {
  getGridDateOperators,
  getGridStringOperators,
  GridAggregationFunction,
  GridColDef,
  GridRenderCellParams,
  GridValueFormatter,
  GridValueGetter,
} from '@mui/x-data-grid-premium'
import 'components/DataGrid/DataGridContext.css'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import { customSortOperator } from 'components/DataGrid/operators/customSortOperator'
import { parseNumeric } from 'components/DataGrid/operators/formatNumbers'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'

import RenderDateCell from 'components/Deals/grid/cells/RenderDateCell'
import RenderStatusCell from 'components/Deals/grid/cells/RenderStatusCell'
import AggregateCurrencyCell from 'components/MTB/grid/cells/AggregateCell'
import RenderOrganizationCell from 'components/MTB/grid/cells/RenderOrganizationCell'
import TeamDetailsCell from 'components/MTB/grid/cells/TeamDetailsCell'
import RenderServiceIcon from 'components/RenderServiceIcons/RenderServiceIcon'
import FlexTypography from 'components/ui/flex-typography'
import { FlexRow } from 'theme/components'
import { DensityStyleTypes, Product, ProductRowFields } from 'types'

const currencyValueFormatter: GridValueFormatter = ({ value }) =>
  value == null
    ? ''
    : (value as number).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })

export const safeSum: GridAggregationFunction<number, number> = {
  label: 'Safe sum',
  apply: ({ values }) => values.reduce((sum, v) => (typeof v === 'number' && !Number.isNaN(v) ? sum + v : sum), 0),
  valueFormatter: currencyValueFormatter,
}
export const renderProductColumns = (
  density: 'compact' | 'standard' | 'comfortable',
  openInfo: (product: ProductRowFields) => void,
  handleRowClick: (row: Product) => void,
  billingModel?: 'points' | 'legacy'
): GridColDef[] => {
  const densityStyles: DensityStyleTypes = getDensityStyles(density)
  const isPoints = billingModel === 'points'

  return [
    {
      field: 'productName',
      headerName: 'Service Name',
      minWidth: 250,
      flex: 1,
      headerAlign: 'left',
      valueGetter: params => params,
      renderCell: params => {
        //@ts-ignore
        if (params?.rowNode?.isAutoGenerated) return null
        return (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => openInfo(params?.row)}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && openInfo(params?.row)}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              height: '100%',
              cursor: 'pointer',
              textDecoration: 'none',
              textUnderlineOffset: 2,
              textAlign: 'left',
            }}
          >
            <RenderServiceIcon
              service={params?.row?.productName ?? null}
              size={{
                width: densityStyles.iconSize + 2,
                height: densityStyles.iconSize + 2,
                fontSize: densityStyles.fontSize,
                marginLeft: 2,
              }}
            />
          </Box>
        )
      },
    },
    // ...(isPoints
    //   ? ([
    //       {
    //         field: 'points',
    //         headerName: 'Points',
    //         type: 'number',
    //         width: 80,
    //         headerAlign: 'left',
    //         aggregable: true,
    //         valueGetter: (_, row) => parseNumeric(row?.points),
    //         renderCell: (params: GridRenderCellParams) => (
    //           <FlexTypography sx={{ fontSize: densityStyles.fontSize }}>
    //             {params.value != null ? params.value : '–'}
    //           </FlexTypography>
    //         ),
    //       },
    //       {
    //         field: 'ratePerPoint',
    //         headerName: '$/pt',
    //         type: 'number',
    //         width: 70,
    //         headerAlign: 'left',
    //         valueGetter: (_, row) => parseNumeric(row?.ratePerPoint),
    //         renderCell: (params: GridRenderCellParams) => (
    //           <FlexTypography sx={{ fontSize: densityStyles.fontSize }}>
    //             {params.value != null ? `$${params.value}` : '–'}
    //           </FlexTypography>
    //         ),
    //       },
    //     ] as GridColDef[])
    //   : []),
    {
      field: 'organizationName',
      headerName: 'Organization',
      renderHeader: () => {
        return (
          <FlexRow extraStyles={{ gap: 1, overflow: 'hidden' }}>
            <CorporateFareIcon fontSize="small" sx={{ color: 'icon.organizations' }} />
            <Typography sx={{ fontSize: 14 }}>Organizations</Typography>
          </FlexRow>
        )
      },
      minWidth: 150,
      flex: 0.7,
      headerAlign: 'left',
      aggregable: false,
      renderCell: params => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {/* <Typography sx={{ fontSize: 12 }}>{params?.value}</Typography> */}
          <RenderOrganizationCell
            params={params}
            densityStyles={densityStyles}
            handleRowClick={handleRowClick}
            toggleExpanded={() => {}}
          />
        </Box>
      ),
    },
    {
      field: 'price',
      headerName: 'Price',
      type: 'number',
      width: 100,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_, row) => parseNumeric(row?.price),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },

    {
      field: 'netTotal',
      headerName: 'Net Total',
      type: 'number',
      width: 100,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_, row) => parseNumeric(row?.netTotal),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'mediaSpend',
      headerName: 'Spend',
      type: 'number',
      width: 100,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_, row) => parseNumeric(row?.mediaSpend),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    // {
    //   field: '_resolvedOwner ',
    //   headerName: 'Owner',
    //   width: 75,
    //   headerAlign: 'center',
    //   aggregable: false,
    //   valueGetter: (_value, row) => {
    //     return row?.owner?.name ?? ''
    //   },
    //   filterOperators: [...getGridStringOperators(), customNotContainsOperator],
    //   sortComparator: customSortOperator,
    //   renderCell: (params: GridRenderCellParams) => {
    //     if (!params?.row?._resolvedOwner) return <ChildNoValueCell densityStyles={densityStyles} />
    //     // OwnerCell expects owner at params.row.owner
    //     const _params = { ...params, row: { ...params.row, owner: params.row._resolvedOwner } }
    //     return <OwnerCell params={_params} densityStyles={densityStyles} />
    //   },
    // },
    {
      field: 'team',
      headerName: 'Team',
      width: 100,
      headerAlign: 'center',
      aggregable: false,
      valueGetter: (_, row) => {
        return row?.team?.map((user: any) => user.name).join(', ') || ''
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => {
        // TeamDetailsCell expects team_details at params.row.team_details
        const _params = { ...params, row: { ...params.row, team_details: params.row?.team || [] } }
        return <TeamDetailsCell params={_params} densityStyles={densityStyles} />
      },
    },
    {
      field: 'dealName',
      headerName: 'Deal',
      renderHeader: params => {
        return (
          <FlexRow extraStyles={{ gap: 1, overflow: 'hidden' }}>
            <AttachMoneyIcon fontSize="small" sx={{ color: 'icon.deals' }} />
            <Typography sx={{ fontSize: 14 }}>Deal</Typography>
          </FlexRow>
        )
      },
      minWidth: 225,
      flex: 0.7,
      headerAlign: 'left',
      aggregable: false,
      renderCell: params => {
        const deal = params?.row?._resolvedDeal
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: '100%',
              cursor: deal ? 'pointer' : 'default',
            }}
            onClick={() => {
              if (deal && params?.row?.PK) {
                handleRowClick(params?.row)
              }
            }}
          >
            <FlexTypography sx={{ fontSize: densityStyles.fontSize }}>{params?.value}</FlexTypography>
          </Box>
        )
      },
    },
    {
      field: 'dealStatus',
      headerName: 'Deal Status',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'dealType',
      headerName: 'Deal Type',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'dealStage',
      headerName: 'Deal Stage',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'contractId',
      headerName: 'Contract ID',
      width: 140,
      headerAlign: 'left',
      aggregable: false,
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <FlexTypography sx={{ fontSize: densityStyles.fontSize }}>{params?.value}</FlexTypography>
        </Box>
      ),
    },
    {
      field: 'phase',
      headerName: 'Phase',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_, row) => {
        const phase = row?.phase || ''
        if (!phase) return ''
        return phase === 'setup' ? 'Setup' : phase.replace('month-', 'Mo ')
      },
      renderCell: (params: GridRenderCellParams) => {
        if (!params?.value) return null
        return <RenderStatusCell params={params} densityStyles={densityStyles} />
      },
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 85,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_, row) => row?.category || row?.category_name || '',
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'subcategory',
      headerName: 'Subcategory',
      width: 85,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (params: GridValueGetter) => params,
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'status',
      headerName: 'Status',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (params: GridValueGetter) => params,
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      type: 'date',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: getGridDateOperators(),
      sortComparator: customSortOperator,
      valueGetter: params => (params ? new Date(params) : null),
      renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      type: 'date',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: getGridDateOperators(),
      sortComparator: customSortOperator,
      valueGetter: params => (params ? new Date(params) : null),
      renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
    },
  ]
}
